import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { ErrorDialog } from '@/components/ui/ErrorDialog';

import { fetchPackageLlmTxt } from '../api';
import { VersionSwitcher } from '../components/VersionSwitcher';
import { navigateTo } from '../router';
import type { LcRouteParams } from '../types';
import { buildRawRoute, buildTreeRoute, displayPackageName } from '../utils';

interface RawLlmTxtPageProps {
  params: LcRouteParams;
}

export function RawLlmTxtPage({ params }: RawLlmTxtPageProps) {
  const [llmTxt, setLlmTxt] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copy = async () => {
    if (!llmTxt) return;
    try {
      await navigator.clipboard.writeText(llmTxt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — text is still selectable */
    }
  };

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const nextValue = await fetchPackageLlmTxt(params.registry, params.name, params.version);
        if (isMounted) {
          setLlmTxt(nextValue);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unable to load raw llm.txt.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, [params.name, params.registry, params.version]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-ink">Raw llm.txt</h2>
          <div className="mt-1 flex items-center gap-2 text-sm text-dim">
            <span>{displayPackageName(params.registry, params.name)}</span>
            <span>·</span>
            <VersionSwitcher params={params} buildRoute={buildRawRoute} />
            <span>· {llmTxt.length.toLocaleString()} chars</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={copy} disabled={isLoading || !llmTxt}>
            {copied ? 'Copied!' : 'Copy'}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => navigateTo(buildTreeRoute(params))}>
            Back to index
          </Button>
        </div>
      </div>

      <div className="lc-code-panel p-4">
        {isLoading ? (
          <div className="p-4 text-sm text-dim">Loading…</div>
        ) : (
          <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap font-mono text-sm text-ink">
            {llmTxt}
          </pre>
        )}
      </div>

      {error && (
        <ErrorDialog
          title="Unable to Load llm.txt"
          message={error}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
}
